import { ordersService } from '@/services/ordersService';
import { formatNomenclatureWithSize } from '@equip/utils';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import {
  buildOutstandingPosReportHtml,
  type OutstandingPoPriceDisplayMode,
  type OutstandingPoItem,
  type OutstandingPosTemplateOptions,
} from '@/reports/templates/outstandingPosTemplate';

const normalizeDate = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const OUTSTANDING_PO_PRICE_DISPLAY_MODE = 'total-line' as OutstandingPoPriceDisplayMode;

const formatLogFormNumber = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Math.trunc(value)).padStart(6, '0');
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (/^\d+$/.test(trimmed)) {
      return trimmed.padStart(6, '0');
    }
    return trimmed;
  }

  return null;
};

export const runOutstandingPosReport = async (
  params: Record<string, unknown> = {},
): Promise<void> => {
  const orders = await ordersService.listOrders();

  const outstandingItems: OutstandingPoItem[] = [];
  const today = new Date();
  const todayMs = today.getTime();

  for (const order of orders) {
    const status = order.entity_data.status ?? 'Pending';
    if (status === 'Completed' || status === 'Cancelled') {
      continue;
    }

    const prDateRaw = order.entity_data.order_date ?? null;
    const prDateNormalized = normalizeDate(prDateRaw);
    const prDateDate = prDateNormalized ? new Date(prDateNormalized) : null;
    const daysOnOrder =
      prDateDate != null
        ? Math.max(0, Math.floor((todayMs - prDateDate.getTime()) / (24 * 3600 * 1000)))
        : null;

    const orderData = order.entity_data ?? {};
    const logFormNumber =
      formatLogFormNumber(orderData.log_form_number) ?? order.entity_identifier ?? order.id;
    const description =
      (orderData.pr_description as string | undefined) ??
      (orderData.description as string | undefined) ??
      null;

    const lineItems = await ordersService.getOrderLineItems(order.id);

    for (const item of lineItems) {
      const ordered = item.entity_data.quantity ?? 0;
      const received =
        item.receipts?.reduce(
          (sum, receipt) => sum + (receipt.entity_data?.quantity_received ?? 0),
          0,
        ) ?? 0;
      const outstanding = ordered - received;

      if (outstanding <= 0) {
        continue;
      }

      const unitPrice =
        item.entity_data.actual_price ?? item.entity_data.unit_price_at_time_of_order ?? null;
      const totalLineValue = unitPrice != null ? unitPrice * ordered : null;
      const price = OUTSTANDING_PO_PRICE_DISPLAY_MODE === 'total-line' ? totalLineValue : unitPrice;

      outstandingItems.push({
        logFormNumber,
        description,
        prDate: prDateNormalized ?? prDateRaw ?? '',
        daysOnOrder,
        lin: item.catalog?.entity_data?.lin ?? null,
        nsn: item.catalog?.entity_data?.nsn ?? null,
        nomenclature: formatNomenclatureWithSize(
          item.catalog?.entity_data?.nomenclature ?? item.catalog?.display_name ?? 'Unknown item',
          item.catalog?.entity_data?.size as string | undefined,
        ),
        qtyReceived: received,
        qtyOrdered: ordered,
        price,
      });
    }
  }

  const options: OutstandingPosTemplateOptions = {
    generatedAt: new Date(),
    items: outstandingItems,
    priceDisplayMode: OUTSTANDING_PO_PRICE_DISPLAY_MODE,
  };

  const html = buildOutstandingPosReportHtml(options);
  outputReport(
    html,
    'outstanding-pos-report.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
